import React, { useEffect, useRef, useState } from "react";
import { sendOtp, resendOtp, verifyOtp } from "../services/otpService";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 60;

/**
 * Phone-number OTP verification modal.
 *
 * Props:
 *  - phone:       10-digit number to verify (required)
 *  - onVerified:  called after a successful verify + Firebase sign-in
 *  - onClose:     called when the user dismisses the modal
 *  - title/subtitle: optional copy overrides
 */
export default function OtpVerifyModal({ phone, onVerified, onClose, title, subtitle }) {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [sending, setSending] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);
  const refs = useRef([]);
  const sentRef = useRef(false); // guard against StrictMode double auto-send

  const code = digits.join("");
  const complete = code.length === OTP_LENGTH;

  // Auto-send on mount
  useEffect(() => {
    if (sentRef.current) return; // StrictMode fires effects twice in dev — send only once
    sentRef.current = true;
    (async () => {
      try {
        await sendOtp(phone);
        setInfo(`OTP sent to +91 ${phone}`);
      } catch (e) {
        setError(e.message || "Could not send OTP.");
      } finally {
        setSending(false);
        refs.current[0]?.focus();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resend cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleChange = (val, i) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[i] = val;
    setDigits(next);
    setError("");
    if (val && i < OTP_LENGTH - 1) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (e, i) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (pasted.length) {
      const next = Array(OTP_LENGTH).fill("");
      pasted.split("").forEach((d, idx) => { next[idx] = d; });
      setDigits(next);
      refs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    }
  };

  const handleVerify = async () => {
    if (!complete || verifying) return;
    setVerifying(true);
    setError("");
    try {
      await verifyOtp(phone, code);
      // Successful OTP = login. Persist so the user is fully signed in
      // (logout button, guards, dashboard) — same as the normal login flow.
      localStorage.setItem("PHONE_NUMBER", phone);
      // Firebase sign-in (inside verifyOtp) fires onAuthStateChanged BEFORE the
      // line above runs, so listeners gating on PHONE_NUMBER (e.g. TurantChat)
      // need this nudge to re-check without a page refresh.
      window.dispatchEvent(new Event("tx-login"));
      onVerified?.();
    } catch (e) {
      setError(e.message || "Incorrect OTP.");
      setDigits(Array(OTP_LENGTH).fill(""));
      refs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(""); setInfo("");
    try {
      await resendOtp(phone);
      setInfo("OTP resent.");
      setCooldown(RESEND_SECONDS);
    } catch (e) {
      setError(e.message || "Could not resend OTP.");
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <button style={closeBtn} onClick={onClose} aria-label="Close">✕</button>

        <div style={iconWrap}>🔒</div>
        <h2 style={titleStyle}>{title || "Verify your number"}</h2>
        <p style={subStyle}>
          {subtitle || <>We've sent a {OTP_LENGTH}-digit code to <strong>+91 {phone}</strong></>}
        </p>

        <div style={{ display: "flex", gap: 10, justifyContent: "center", margin: "18px 0 6px" }} onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (refs.current[i] = el)}
              type="tel"
              inputMode="numeric"
              maxLength={1}
              value={d}
              disabled={sending || verifying}
              aria-label={`OTP digit ${i + 1}`}
              autoComplete={i === 0 ? "one-time-code" : "off"}
              onChange={(e) => handleChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              className="clarity-mask"
              style={otpBox}
            />
          ))}
        </div>

        {error && <p style={errStyle} role="alert">{error}</p>}
        {!error && info && <p style={infoStyle}>{info}</p>}

        <button
          style={{ ...verifyBtn, opacity: complete && !verifying ? 1 : 0.55, cursor: complete && !verifying ? "pointer" : "not-allowed" }}
          onClick={handleVerify}
          disabled={!complete || verifying}
        >
          {verifying ? "Verifying…" : "Verify & continue"}
        </button>

        <div style={{ marginTop: 14, fontSize: "0.82rem", color: "#667781" }}>
          Didn't get it?{" "}
          <button
            onClick={handleResend}
            disabled={cooldown > 0 || sending}
            style={{
              background: "none", border: "none", padding: 0, fontWeight: 700,
              color: cooldown > 0 ? "#aaa" : "var(--color-primary, #FF621A)",
              cursor: cooldown > 0 ? "default" : "pointer",
            }}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── inline styles (self-contained, no external CSS dependency) ── */
const overlay = {
  position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
  backdropFilter: "blur(2px)", display: "flex", alignItems: "center",
  justifyContent: "center", zIndex: 2000, padding: 20,
};
const modal = {
  background: "#fff", borderRadius: 22, padding: "32px 24px 26px",
  width: "100%", maxWidth: 380, textAlign: "center", position: "relative",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
};
const closeBtn = {
  position: "absolute", top: 14, right: 16, background: "none", border: "none",
  fontSize: "1.1rem", color: "#94a3b8", cursor: "pointer",
};
const iconWrap = {
  width: 64, height: 64, borderRadius: 18, background: "#fff4ed",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: "1.8rem", margin: "0 auto 16px",
};
const titleStyle = { fontSize: "1.25rem", fontWeight: 800, color: "#1a1a2e", marginBottom: 6 };
const subStyle = { fontSize: "0.88rem", color: "#667781", lineHeight: 1.5, margin: 0 };
const otpBox = {
  width: 52, height: 60, borderRadius: 12, border: "1.5px solid #e2e8f0",
  fontSize: "1.5rem", fontWeight: 700, textAlign: "center", outline: "none",
  color: "#1a1a2e", background: "#f8fafc",
};
const errStyle = { color: "#dc2626", fontSize: "0.82rem", fontWeight: 600, margin: "8px 0 0", whiteSpace: "pre-line", wordBreak: "break-word", textAlign: "left" };
const infoStyle = { color: "#0f9d58", fontSize: "0.82rem", fontWeight: 600, margin: "8px 0 0" };
const verifyBtn = {
  marginTop: 18, width: "100%", padding: "14px", borderRadius: 14, border: "none",
  background: "linear-gradient(135deg, #FF621A 0%, #e0521a 100%)", color: "#fff",
  fontSize: "1rem", fontWeight: 700,
};
